import { KalturaAssetReferenceType, KalturaAssetType } from '@kaltura-ott/ott-client-types/api/types';
import {
  cloneObject,
  ConnectivityType,
  endOfDay,
  FileFormatType,
  getAssetsByExternalIds,
  getEpgDataV2,
  getMediaAsset,
  getRecordingProgram,
  KuxEpisodeType,
  KuxLinearType,
  KuxMovieType,
  KuxProgramType,
  startOfDay,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import { onPlaybackStop } from '../../../rhino/data/playback';
import { ParentalPinType } from '../../../types/stores/parental-pin-store-type';
import { featureEnabler } from '../../../utils';
import { convertRhinoErrorToTvpil } from '../../../utils/error';
import { errorHandling } from '../../../utils/error-handling';
import type { AppStore } from '../../app/app-store';
import type { LastWatchedLinearStore } from '../../channelStats/last-watched-linear-store';
import { DmsStore } from '../../dms';
import { Episode, Linear, Movie, Program } from '../../entities';
import { creatorEntityStore } from '../../entities/entities.service';
import { PlayableAsset } from '../../entities/PlayableAsset';
import { ErrorPriority } from '../../error';
import { ParentalPinStore } from '../../parentalPin';
import type { TransactionStore } from '../../transaction/transaction-store';
import { UserStore } from '../../user';

import {
  addDataToPlayoutLinear,
  addDataToPlayoutProgram,
  addDataToPlayoutVod,
  getLinear,
  PlayoutStoreV2Type,
  getNetworkModeWithCache,
} from './playout-store.service';

export interface PlayoutStoreV2InitArgs {
  /** id of asset */
  id?: number;
  /** externalId of asset */
  externalId?: string;
  /** playableAsset */
  playableAsset?: PlayableAsset;
  /** mode of playback */
  type: PlayoutStoreV2Type;
  /** for catchup mode ability to play the next program. Always true for startover  */
  autoplayNextProgram?: boolean;
  /** save channel as last watched */
  saveLastWatched?: boolean;
  /** livetimestamp */
  liveTimestamp?: number;
  /** is playout store for maxi player */
  isMaxiPlayer?: boolean;
  /** mosaic data */
  mosaic?: {
    /** externalId of mosaic hidden channel */
    externalId?: string;
    /** true if it is needed to load first hidden stream in case of externalId or id parameter is of mosaic asset */
    loadFirstSubStream?: boolean;
  };
  /** load mosaic current program */
  loadMosaic?: boolean;

  /** is the current asset launched using autoplay */
  fromAutoplayMode?: boolean;
}

@singleton()
export class PlayoutStoreV2 {
  static moduleName = 'PlayoutStoreV2';

  @observable currentAsset?: PlayableAsset;
  @observable subAsset?: PlayableAsset;
  @observable nextProgram?: Program;
  @observable type?: PlayoutStoreV2Type;
  @observable autoplayNextProgram = false;

  public isLoading = false;
  private dmsStore: DmsStore;
  private lastWatchedLinear: LastWatchedLinearStore;
  private userStore: UserStore;

  private loadingNextProgramTimer?: NodeJS.Timeout;

  private initiatedIdOrExternalId?: number | string;

  private fromAutoplayMode = false;

  isMaxiPlayer = false;
  private prevIsMaxiPlayer?: boolean | undefined;

  constructor() {
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
    this.userStore = container.resolve<UserStore>('userStore');
  }

  private get appStore() {
    return container.resolve<AppStore>('appStore');
  }

  private get transactionStore() {
    return container.resolve<TransactionStore>('transactionStore');
  }

  private get isMosaicEnabled() {
    return this.dmsStore.dms.mosaicInfo.isMosaicSupported;
  }

  private async getPlayableAsset(type: PlayoutStoreV2Type, id: number) {
    if (type === PlayoutStoreV2Type.NPVR) {
      return await lastValueFrom(
        getRecordingProgram([id.toString()], KalturaAssetType.epg, false, featureEnabler.variation('NEW_NPVR', false)),
      );
    } else {
      return await lastValueFrom(
        getMediaAsset<KuxProgramType | KuxMovieType | KuxLinearType | KuxEpisodeType>(
          id,
          [PlayoutStoreV2Type.CATCHUP, PlayoutStoreV2Type.START_OVER, PlayoutStoreV2Type.NPVR].includes(type)
            ? KalturaAssetReferenceType.epgInternal
            : KalturaAssetReferenceType.media,
        ),
      );
    }
  }

  @action
  @errorHandling({
    callback: function handleEpgInitError(this: PlayoutStoreV2, error) {
      convertRhinoErrorToTvpil(error);
      this.isLoading = false;
    },
    type: ErrorPriority.high,
  })
  async init({
    id,
    externalId,
    playableAsset,
    type,
    autoplayNextProgram = false,
    fromAutoplayMode = false,
    saveLastWatched = false,
    liveTimestamp,
    isMaxiPlayer = false,
    mosaic,
    loadMosaic = true,
  }: PlayoutStoreV2InitArgs): Promise<void> {
    this.isLoading = true;

    if (this.loadingNextProgramTimer) {
      clearTimeout(this.loadingNextProgramTimer);
    }

    if (!type) {
      throw new Error('PlayoutStoreV2 - type is undefined');
    }

    if (!id && !playableAsset && !externalId) {
      throw new Error('PlayoutStoreV2 - id or externalId, or playableAsset are undefined');
    }

    this.isMaxiPlayer = isMaxiPlayer;

    this.fromAutoplayMode = fromAutoplayMode;

    let newCurrentAsset: PlayableAsset | undefined;
    let newSubAsset: PlayableAsset | undefined;

    if (playableAsset) {
      newCurrentAsset = cloneObject(playableAsset);
      this.initiatedIdOrExternalId = playableAsset.id;
    } else {
      let newKuxPlayableAsset;

      if (id) {
        this.initiatedIdOrExternalId = id;
        newKuxPlayableAsset = await this.getPlayableAsset(type, id);
      } else if (externalId) {
        this.initiatedIdOrExternalId = externalId;
        newKuxPlayableAsset = (await lastValueFrom(getAssetsByExternalIds([externalId])))?.[0];
      }

      newCurrentAsset = newKuxPlayableAsset ? (creatorEntityStore(newKuxPlayableAsset) as PlayableAsset) : undefined;
    }

    if (!newCurrentAsset) {
      return;
    }

    /**
     * second condition is needed to clone linear asset because
     * we should use new object for playback to not be affected by display logic in rails
     */
    if (newCurrentAsset instanceof Linear) {
      newCurrentAsset = cloneObject(newCurrentAsset);
    }

    if (newCurrentAsset instanceof Linear) {
      await this.setupLinear(
        newCurrentAsset,
        liveTimestamp,
        Boolean(
          (!newCurrentAsset.currentProgram || newCurrentAsset.currentProgram?.data.mosaicInfo?.type === 'mosaic') &&
            (mosaic?.externalId || mosaic?.loadFirstSubStream),
        ),
      );

      if (newCurrentAsset.currentProgram?.data.mosaicInfo?.isMosaicEvent === '1' && loadMosaic) {
        if (
          newCurrentAsset.currentProgram?.data.mosaicInfo.type === 'mosaic' &&
          (mosaic?.externalId || mosaic?.loadFirstSubStream)
        ) {
          const hiddenExternalId = mosaic?.loadFirstSubStream
            ? newCurrentAsset.currentProgram?.data.mosaicInfo.mosaicChannelsInfo?.[0].channelExternalId
            : mosaic?.externalId;

          if (!hiddenExternalId) {
            console.warn(`Program id=${newCurrentAsset.currentProgram?.data.id} has incorrect mosaicChannelsInfo`);
            return;
          }

          const hiddenChannel = (await lastValueFrom(getAssetsByExternalIds([hiddenExternalId])))?.[0] as KuxLinearType;

          if (!hiddenChannel) {
            console.warn(`Program id=${newCurrentAsset.currentProgram?.data.id} has incorrect mosaicChannelsInfo`);
            return;
          }

          newSubAsset = cloneObject(creatorEntityStore(hiddenChannel));
          const mosaicProgram = newCurrentAsset.currentProgram;

          await this.setupLinear(newSubAsset as Linear, liveTimestamp);
          const mosaicChannel = await getLinear(mosaicProgram.data.linearAssetId);

          if (mosaicChannel) {
            await this.setupLinear(mosaicChannel, liveTimestamp, true);
            newCurrentAsset = mosaicChannel;
          }
        } else if (
          newCurrentAsset.currentProgram.data.mosaicInfo.mosaicProgramExternalId &&
          (newCurrentAsset.currentProgram?.data.mosaicInfo.type === 'hidden' ||
            newCurrentAsset.currentProgram?.data.mosaicInfo.type === 'video')
        ) {
          newSubAsset = newCurrentAsset;
          const mosaicProgram = (
            await lastValueFrom(
              getAssetsByExternalIds([newCurrentAsset.currentProgram.data.mosaicInfo.mosaicProgramExternalId]),
            )
          )?.[0] as KuxProgramType;

          if (!mosaicProgram) {
            console.warn(
              `mosaicProgramExternalId ${newCurrentAsset.currentProgram.data.mosaicInfo.mosaicProgramExternalId} is incorrect`,
            );
            return;
          }

          const mosaicChannel = await getLinear(mosaicProgram.linearAssetId);

          if (mosaicChannel) {
            await this.setupLinear(mosaicChannel, liveTimestamp, true);
            newCurrentAsset = mosaicChannel;
          }
        }
      }

      if (
        newCurrentAsset.isPlayable &&
        newCurrentAsset?.data?.id &&
        !newCurrentAsset.rules?.length &&
        saveLastWatched
      ) {
        const currentProgram = (newCurrentAsset as Linear).currentProgram;
        let subChannel;

        if (
          currentProgram?.data.mosaicInfo?.isMosaicEvent === '1' &&
          currentProgram?.data.mosaicInfo.type === 'mosaic'
        ) {
          subChannel = newSubAsset as Linear;
        }

        await this.lastWatchedLinear.saveLastWatched({
          linear: newCurrentAsset as Linear,
          ...(subChannel
            ? {
                subLinearExternalId: subChannel?.data.externalId,
                subProgramExternalId: subChannel.currentProgram?.data.externalId,
              }
            : undefined),
        });
      }
    }

    if (newCurrentAsset instanceof Program) {
      if (newCurrentAsset?.data.mosaicInfo?.isMosaicEvent === '1' && loadMosaic) {
        if (newCurrentAsset.data.mosaicInfo.type === 'mosaic' && (mosaic?.externalId || mosaic?.loadFirstSubStream)) {
          const hiddenExternalId = mosaic?.loadFirstSubStream
            ? newCurrentAsset?.data.mosaicInfo.mosaicChannelsInfo?.[0].channelExternalId
            : mosaic?.externalId;
          const hiddenProgram = // @ts-expect-error TODO Fix
            (await lastValueFrom(getAssetsByExternalIds([hiddenExternalId])))?.[0] as KuxProgramType;
          newSubAsset = creatorEntityStore(hiddenProgram) as Program;
          await addDataToPlayoutProgram(newSubAsset as Program, type, this.fromAutoplayMode);
        } else if (
          newCurrentAsset.data.mosaicInfo.mosaicProgramExternalId &&
          (newCurrentAsset?.data.mosaicInfo.type === 'hidden' || newCurrentAsset?.data.mosaicInfo.type === 'video')
        ) {
          newSubAsset = newCurrentAsset;
          await Promise.all([
            (async () => {
              const mosaicProgram = (
                await lastValueFrom(
                  // @ts-expect-error TODO Fix
                  getAssetsByExternalIds([newCurrentAsset.data.mosaicInfo?.mosaicProgramExternalId]),
                )
              )?.[0] as KuxProgramType;

              if (!mosaicProgram) {
                console.warn(
                  `mosaicProgramExternalId ${newCurrentAsset.data.mosaicInfo?.mosaicProgramExternalId} is incorrect`,
                );
                return;
              }

              newCurrentAsset = creatorEntityStore(mosaicProgram);
            })(),
            addDataToPlayoutProgram(newSubAsset as Program, type, this.fromAutoplayMode),
          ]);
        }
      }

      await addDataToPlayoutProgram(newCurrentAsset as Program, type, this.fromAutoplayMode);
    }

    if (newCurrentAsset instanceof Movie && type !== PlayoutStoreV2Type.WELCOME_POPUP) {
      await addDataToPlayoutVod(newCurrentAsset, type);
    }

    if (newCurrentAsset instanceof Episode) {
      await addDataToPlayoutVod(newCurrentAsset, type);
    }

    if (
      // @ts-expect-error TODO Fix
      this.currentAsset?.id !== newCurrentAsset.id &&
      (newCurrentAsset instanceof Linear || newCurrentAsset instanceof Program)
    ) {
      await this.transactionStore.checkFFSubscriptions();
    }

    this.nextProgram = undefined;
    // @ts-expect-error TODO Fix
    await this.setNewAsset(newCurrentAsset, type, autoplayNextProgram, newSubAsset);
    this.isLoading = false;
  }

  @computed get isStartOver(): boolean {
    return this.type === PlayoutStoreV2Type.START_OVER;
  }

  private checkLinearFFEnabled(linear: Linear) {
    if (!linear.data.blockFastForward) {
      return true;
    }
    return this.transactionStore.isUserEntitledToFF;
  }

  @computed get FFEnabled(): boolean {
    if (this.currentAsset instanceof Program) {
      if (!this.currentAsset.data.enableTrickPlay || !this.currentAsset.linear) {
        return false;
      }
      return this.checkLinearFFEnabled(this.currentAsset.linear);
    }

    if (this.currentAsset instanceof Linear) {
      if (!this.currentAsset.currentProgram?.data.enableTrickPlay) {
        return false;
      }
      return this.checkLinearFFEnabled(this.currentAsset);
    }

    return true;
  }

  @action
  @errorHandling()
  stopPlayback(): void {
    this.appStore.resetBookmarkDependentWidgets();
    onPlaybackStop();
  }

  /**
   * Temporary solution.
   * The function must be called before turning on the player via digitWizard.
   * It is necessary for the lastwatch via digitWizard to be applied only to the mainHero / epg
   */
  @action
  @errorHandling()
  openByDigitWizard() {
    this.lastWatchedLinear.clearCurrentHeroRail();
  }

  @action
  reset() {
    this.currentAsset = undefined;
    this.type = undefined;
    this.subAsset = undefined;
  }

  async isMulticastPlayback(): Promise<boolean> {
    if (!this.currentAsset) {
      return false;
    }

    if (!this.hasMulticastFiles(this.currentAsset)) {
      return false;
    }

    return this.isIpTvMode();
  }

  hasMulticastFiles(asset: PlayableAsset) {
    return Boolean(
      asset.data.mediaFilesTypes?.find((mediaFileType) =>
        Object.values(this.dmsStore.dms.filesFormat).find(
          (fileFormat) => fileFormat[0] === mediaFileType && fileFormat[1] === FileFormatType.multicast,
        ),
      ),
    );
  }

  async isIpTvMode(): Promise<boolean> {
    if (!this.dmsStore.dms.multicastParams.multicastIndicationKey) {
      return false;
    }

    const networkMode = await getNetworkModeWithCache(
      this.dmsStore.dms.multicastParams.multicastEndpoint,
      this.dmsStore.dms.multicastParams.multicastIndicationKey,
    );

    return networkMode === ConnectivityType.iptv;
  }

  getCurrentProgram(): Program | undefined {
    if (this.currentAsset instanceof Linear) {
      return this.currentAsset.currentProgram;
    }

    if (this.currentAsset instanceof Program) {
      return this.currentAsset;
    }

    return;
  }

  getCurrentPermanentProgram(): KuxProgramType | undefined {
    return this.getCurrentProgram()?.data.permanentProgram;
  }

  getCurrentPermanentLinearAssetId(): number | undefined {
    return this.getCurrentPermanentProgram()?.linearAssetId;
  }

  isMosaicPlayback(): boolean {
    return this.getCurrentProgram()?.data.mosaicInfo?.type === 'mosaic';
  }

  getCurrentProgramId(): number | undefined {
    return this.getCurrentProgram()?.id;
  }

  private saveIsMaxiPlayerState(isMaxiPlayerState: boolean) {
    this.prevIsMaxiPlayer = isMaxiPlayerState;
  }

  private async setupLinear(linear: Linear, liveTimestamp?: number, loadMosaicPrograms?: boolean) {
    await addDataToPlayoutLinear(linear, liveTimestamp, loadMosaicPrograms);

    this.loadNextPrograms(linear, liveTimestamp);

    if (this.currentAsset?.id !== linear.id && this.userStore.userData) {
      this.appStore?.channelStats?.saveChannelStats(linear.id, this.userStore.userData.id);
    }

    this.saveIsMaxiPlayerState(this.isMaxiPlayer);
    return linear;
  }

  private loadNextPrograms(linear: Linear, liveTimestamp?: number) {
    if (linear.currentProgram) {
      if (this.loadingNextProgramTimer) {
        clearTimeout(this.loadingNextProgramTimer);
      }

      const duration = linear.currentProgram.data.endDate - Date.now();

      const timeToUpdate = duration > 60000 ? Math.floor(Math.random() * duration) + 1 : 0;

      this.loadingNextProgramTimer = setTimeout(() => {
        if (!linear.currentProgram) {
          return;
        }

        lastValueFrom(
          getEpgDataV2(
            {
              startTime: startOfDay(new Date(linear.currentProgram?.data.endDate)),
              endTime: endOfDay(new Date(linear.currentProgram?.data.endDate)),
              linearIdList: [linear.id],
              loadMosaicPrograms: this.dmsStore.dms.mosaicInfo?.isMosaicSupported,
            },
            false,
            liveTimestamp,
            true,
          ),
          {
            defaultValue: null,
          },
        );
      }, timeToUpdate);
    }
  }

  // checking that the asset is up to date
  private isCurrentAsset(newCurrentAsset: PlayableAsset): boolean {
    return (
      newCurrentAsset.id === this.initiatedIdOrExternalId ||
      (newCurrentAsset instanceof Linear && newCurrentAsset.data.externalId === this.initiatedIdOrExternalId)
    );
  }

  /**
   *  Set new observable properties
   */
  @action
  private async setNewAsset(
    newCurrentAsset: PlayableAsset,
    type: PlayoutStoreV2Type,
    autoplayNextProgram: boolean,
    newSubAsset: PlayableAsset | undefined,
  ) {
    if (!this.isCurrentAsset(newCurrentAsset) && !newSubAsset) {
      return;
    }
    this.currentAsset = newCurrentAsset;

    //depreciated
    if (!this.appStore.params.useParentalPinStoreDepreciated) {
      const isPastProgram = this.currentAsset instanceof Program && this.currentAsset?.programState === 'PAST';

      if (!this.currentAsset.isAdult && (this.isMaxiPlayer || isPastProgram)) {
        const parentalPinStore = container.resolve<ParentalPinStore>('parentalPinStore');
        parentalPinStore.clearParentalPin(ParentalPinType.adult);
      }
      this.appStore.parentalPinStore.originalStore.lastParentalPinEnteredValue = Math.min(
        this.appStore.parentalPinStore.originalStore.lastParentalPinEnteredValue,
        //KUXEXT-23949: have to get parentalRating from program
        (this.currentAsset instanceof Linear
          ? // @ts-expect-error TODO Fix
            this.currentAsset.currentProgram.parentalRating
          : this.currentAsset.parentalRating) || 0,
      );
    }

    this.subAsset = newSubAsset;

    this.type = type;
    this.autoplayNextProgram = autoplayNextProgram;
  }
}
